import { initializeApp } from "firebase/app";
import { Timestamp } from "firebase/firestore";
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  onIdTokenChanged,
  signOut,
} from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  connectStorageEmulator,
  deleteObject,
} from "firebase/storage";

import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import firebaseConfig from "./config.json";

const env = process.env.REACT_APP_ENV;
const app = initializeApp(firebaseConfig[env]);
const db = getFirestore(app);
const storage = getStorage(app);

// Initialize Firebase Authentication
const auth = getAuth(app);

// Connect to emulators
if (env === "localhost") {
  connectAuthEmulator(auth, "http://localhost:9099");
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
}

const authTokenKey = "authToken";
let currentUser;

const clearTokens = () => {
  localStorage.removeItem(authTokenKey);
  sessionStorage.removeItem(authTokenKey);
};

// If we switch to a backend that returns token we will need to use
// token here instead of just a boolean
const saveLoggedInSession = (loggedIn) => {
  if (localStorage.getItem(authTokenKey)) {
    localStorage.setItem(authTokenKey, loggedIn);
  }

  if (sessionStorage.getItem(authTokenKey)) {
    sessionStorage.setItem(authTokenKey, loggedIn);
  }
};

// Listen for when auth token changes and update the local storage
onIdTokenChanged(auth, async (user) => {
  if (user) {
    try {
      currentUser = user;
      const newToken = await user.getIdToken();
      console.log("Token refreshed:", newToken);
      saveLoggedInSession(true);
    } catch (error) {
      console.error("Failed to update token:", error);
    }
  } else {
    // Clear tokens if user logs out
    clearTokens();
  }
});

const getStorageToken = () => {
  return (
    localStorage.getItem(authTokenKey) || sessionStorage.getItem(authTokenKey)
  );
};

const timestampToDate = (timestamp) => {
  if (!timestamp) {
    console.warn("No timestamp provided");
    return "";
  }

  let date;

  // Check for Firestore Timestamp
  if (typeof timestamp.toDate === "function") {
    date = timestamp.toDate();
  }
  // Check for seconds and nanoseconds structure
  else if (timestamp._seconds && typeof timestamp._seconds === "number") {
    date = new Date(timestamp._seconds * 1000);
  } else {
    console.warn("Invalid timestamp format", timestamp);
  }
  return date;
};

const timestampToDateString = (timestamp) => {
  const date = timestampToDate(timestamp);
  if (!date) {
    return;
  }
  const formattedDate = date.toLocaleDateString(); // Format as needed
  return formattedDate;
};

const timestampToTimeAgo = (timestamp) => {
  if (!timestamp) {
    return "N/A";
  }
  const date =
    timestamp instanceof Timestamp
      ? timestamp.toDate()
      : new Date(timestamp._seconds * 1000 + timestamp._nanoseconds / 1e6);

  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 0) {
    return "in the future";
  }

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [unit, value] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / value);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
};

const uploadDocument = async (file, collection = "images", prefix = "post") => {
  try {
    const uniqueFileName = `${prefix}_${
      (currentUser && currentUser.uid) || "guest"
    }_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `${collection}/${uniqueFileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

const deleteDocument = async (documentPath) => {
  try {
    if (!documentPath) return;
    const documentRef = ref(storage, documentPath);
    await deleteObject(documentRef);
    console.log("Document deleted successfully");
  } catch (error) {
    console.error("Error deleting image:", error);
  }
};

export {
  auth,
  signInWithEmailAndPassword,
  clearTokens,
  signOut,
  authTokenKey,
  saveLoggedInSession,
  getStorageToken,
  db,
  timestampToDateString,
  uploadDocument,
  timestampToTimeAgo,
  deleteDocument,
  timestampToDate,
};
